import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, ShowerHeadIcon, BathIcon, ChevronDownIcon, DropletsIcon, FootprintsIcon, DoorOpenIcon, ThermometerSunIcon, PaletteIcon, SparklesIcon } from 'lucide-react';
import { KohlerQuoteForm } from './KohlerQuoteForm';
interface KohlerLandingPageProps {
  initialProduct?: string;
  onClose: () => void;
}
export function KohlerLandingPage({
  initialProduct,
  onClose
}: KohlerLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>(initialProduct || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialProduct) {
      setSelectedProduct(initialProduct);
    }
  }, [initialProduct]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedProduct) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <KohlerQuoteForm initialZip={zipCode} productSlug={selectedProduct} onClose={onClose} data-id="element-1808" />;
  }
  const products = [{
    id: 'showers',
    name: 'Showers',
    icon: <ShowerHeadIcon className="w-6 h-6" data-id="element-1809" />,
    desc: 'Custom Walk-In Showers'
  }, {
    id: 'walk-in-baths',
    name: 'Walk-In Baths',
    icon: <BathIcon className="w-6 h-6" data-id="element-1810" />,
    desc: 'Safer, Comfortable Tubs'
  }];
  const productLabel = products.find(p => p.id === selectedProduct)?.name || '';
  const heroImages: Record<string, string> = {
    showers: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=1920&q=80',
    'walk-in-baths': 'https://cdn.magicpatterns.com/uploads/bCZvQNnV4PMPg89sXdLggC/Kolher_baths.webp'
  };
  const heroImage = heroImages[selectedProduct] || 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1920&q=80';
  const heroHeadlines: Record<string, string> = {
    showers: 'Custom Walk-In Shower and Bath Remodels',
    'walk-in-baths': 'Safer, More Comfortable Walk-In Tubs'
  };
  const heroHeadline = selectedProduct ? heroHeadlines[selectedProduct] : 'Premium Showers & Walk-In Baths';
  const heroDescriptions: Record<string, string> = {
    showers: 'Transform your bathroom with a custom Reveal by KOHLER shower. Installed in as little as a day by certified professionals.',
    'walk-in-baths': 'Experience the safety and comfort of a KOHLER Walk-In Bath. Featuring whirlpool jets, a low 3-inch step-in, and a wide door for easy entry.'
  };
  const heroDescription = selectedProduct ? heroDescriptions[selectedProduct] : 'Connect with a KOHLER expert near you for a seamless bath remodel. Built on 150 years of expertise.';
  const offerBanners: Record<string, string> = {
    showers: '$1,000 Off Your Shower Remodel + No Payments & No Interest for 12 Months†',
    'walk-in-baths': '50% Off Installation + No Interest for 12 Months†'
  };
  const offerBanner = selectedProduct ? offerBanners[selectedProduct] : 'Installed in as little as a day • Special financing available†';
  // Dynamic branding based on selection
  const isShower = selectedProduct === 'showers';
  const brandColor = isShower ? '#C5D93D' : '#C5A55A'; // Lime green for showers, Gold for baths
  const brandDark = isShower ? '#9EAE31' : '#9E8448';
  const phone = isShower ? '(800) 704-9847' : '(800) 384-9034';
  const renderHeaderBrand = () => {
    if (isShower) {
      return <div className="flex flex-col" data-id="element-1811">
          <span className="text-2xl md:text-3xl font-bold text-white tracking-tight leading-none" data-id="element-1812">
            Reveal
          </span>
          <span className="text-[10px] md:text-xs font-medium text-white tracking-widest ml-1" data-id="element-1813">
            by KOHLER.
          </span>
        </div>;
    } else if (selectedProduct === 'walk-in-baths') {
      return <div className="flex items-baseline gap-1" data-id="element-1814">
          <span className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-1815">
            KOHLER.
          </span>
          <span className="text-lg md:text-xl font-light text-white" data-id="element-1816">
            Walk-In Bath
          </span>
        </div>;
    } else {
      return <div className="flex flex-col" data-id="element-1817">
          <div className="flex items-baseline gap-2" data-id="element-1818">
            <span className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-1819">
              KOHLER.
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-300" data-id="element-1820">
            Showers &bull; Walk-In Baths
          </span>
        </div>;
    }
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-1821">
      {/* Header */}
      <header className="bg-black py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-1822">
        {renderHeaderBrand()}
      </header>

      {/* Offer Banner */}
      <section className="py-3 border-b border-gray-200" style={{
      backgroundColor: brandColor
    }} data-id="element-1823">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1824">
          <h2 className="text-sm md:text-base font-bold text-black tracking-tight" data-id="element-1825">
            {offerBanner}
          </h2>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-1826">
        <div className="absolute inset-0 z-0" data-id="element-1827">
          <img src={heroImage} alt="Kohler bathroom" className="w-full h-full object-cover object-center" data-id="element-1828" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-1829" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1830">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1831">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1832">
              <span className="text-sm font-bold tracking-widest uppercase mb-4 block" style={{
              color: brandColor
            }} data-id="element-1833">
                Installed in as little as a day
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-1834">
                {heroHeadline}
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-1835">
                {heroDescription}
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-1836">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-1837">
                {selectedProduct === 'walk-in-baths' ? <div className="bg-[#C5A55A] p-6 text-center" data-id="element-1838">
                    <h2 className="text-3xl font-bold text-black mb-1" data-id="element-1839">
                      50% Off Installation
                    </h2>
                    <p className="text-black font-medium text-sm" data-id="element-1840">
                      + No Interest for 12 Months†
                    </p>
                  </div> : <div className="bg-black p-6 text-center" data-id="element-1841">
                    <h2 className="text-2xl font-bold text-white" data-id="element-1842">
                      See If You Qualify for Local Discounts
                    </h2>
                  </div>}

                <div className="p-8" data-id="element-1843">
                  {/* Product dropdown (only if no initialProduct) */}
                  {!initialProduct && <div className="mb-6 relative" ref={dropdownRef} data-id="element-1844">
                      <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-gray-50 ${selectedProduct ? 'border-black' : 'border-gray-200'} ${dropdownOpen ? 'border-black shadow-md' : 'hover:border-gray-300'}`} data-id="element-1845">
                        <div className="flex items-center gap-3" data-id="element-1846">
                          {selectedProduct ? <>
                              <span className="text-black" data-id="element-1847">
                                {products.find(p => p.id === selectedProduct)?.icon}
                              </span>
                              <span className="font-bold text-black text-lg" data-id="element-1848">
                                {products.find(p => p.id === selectedProduct)?.name}
                              </span>
                            </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-1849">
                              What are you looking for?
                            </span>}
                        </div>
                        <ChevronDownIcon className={`w-5 h-5 text-black transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-1850" />
                      </button>

                      <AnimatePresence data-id="element-1851">
                        {dropdownOpen && <motion.div initial={{
                      opacity: 0,
                      y: -8,
                      scaleY: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scaleY: 1
                    }} exit={{
                      opacity: 0,
                      y: -8,
                      scaleY: 0.95
                    }} transition={{
                      duration: 0.15,
                      ease: 'easeOut'
                    }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-gray-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-1852">
                            {products.map(prod => <button key={prod.id} onClick={() => {
                        setSelectedProduct(prod.id);
                        setDropdownOpen(false);
                      }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedProduct === prod.id ? 'bg-gray-50 text-black' : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-black'} border-b border-gray-100 last:border-b-0`} data-id="element-1853">
                                <span className={`${selectedProduct === prod.id ? 'text-black' : 'text-gray-400'}`} data-id="element-1854">
                                  {prod.icon}
                                </span>
                                <span className="font-bold text-lg" data-id="element-1855">
                                  {prod.name}
                                </span>
                                {selectedProduct === prod.id && <CheckCircle2Icon className="w-5 h-5 text-black ml-auto" data-id="element-1856" />}
                              </button>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>}

                  {/* Show selected product badge when pre-selected */}
                  {initialProduct && <div className="flex items-center justify-center gap-2 mb-6 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5" data-id="element-1857">
                      {products.find(p => p.id === initialProduct)?.icon}
                      <span className="font-bold text-black" data-id="element-1858">
                        {productLabel}
                      </span>
                    </div>}

                  <p className="text-sm text-gray-600 mb-3 text-center" data-id="element-1859">
                    {selectedProduct === 'walk-in-baths' ? 'Find out how much a walk-in bath remodel costs in your area.' : 'Find out how much a shower or bath remodel costs in your area.'}
                  </p>

                  {/* Zip Code Input */}
                  <div className="relative mb-6" data-id="element-1860">
                    <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Zip Code" className="w-full h-14 px-6 text-xl font-medium bg-white border-2 border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-black focus:ring-1 focus:ring-black transition-colors" maxLength={5} data-id="element-1861" />
                    {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-1862">
                        <CheckCircle2Icon className="w-6 h-6" data-id="element-1863" />
                      </div>}
                  </div>

                  <button onClick={handleContinue} disabled={!isValidZip || !selectedProduct} className={`w-full h-14 rounded-lg font-bold text-lg text-black transition-all shadow-md ${isValidZip && selectedProduct ? 'hover:opacity-90 cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'}`} style={{
                  backgroundColor: isValidZip && selectedProduct ? brandColor : undefined
                }} data-id="element-1864">
                    Get Started
                  </button>

                  {/* Trust Badges */}
                  <div className="mt-6 flex items-center justify-center gap-4" data-id="element-1865">
                    <div className="flex items-center gap-2" data-id="element-1866">
                      <ShieldCheckIcon className="w-5 h-5 text-gray-400" data-id="element-1867" />
                      <span className="text-xs font-medium text-gray-500" data-id="element-1868">
                        Secure & Confidential
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Kohler Section */}
      <section className="py-20 bg-white border-b border-gray-100" data-id="element-1869">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1870">
          <div className="text-center mb-16" data-id="element-1871">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1872">
              Why Choose {isShower ? 'Reveal by KOHLER' : 'KOHLER Walk-In Bath'}
              ?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1873">
            {selectedProduct === 'walk-in-baths' ? [{
            title: 'Hydrotherapy Jets',
            desc: 'Powerful whirlpool jets for soothing relief and targeted massage.',
            icon: <DropletsIcon className="w-8 h-8" data-id="element-1874" />
          }, {
            title: 'Ultra-Low 3" Step-In',
            desc: 'Safely enter and exit your bath with one of the lowest thresholds available.',
            icon: <FootprintsIcon className="w-8 h-8" data-id="element-1875" />
          }, {
            title: 'Extra-Wide Door',
            desc: 'Easy access with a 100% leak-proof seal guaranteed for life.',
            icon: <DoorOpenIcon className="w-8 h-8" data-id="element-1876" />
          }, {
            title: 'Heated Backrest',
            desc: 'Stay warm and comfortable while the tub fills and drains.',
            icon: <ThermometerSunIcon className="w-8 h-8" data-id="element-1877" />
          }].map((feature, idx) => <motion.div key={idx} className="bg-gray-50 p-8 rounded-2xl text-center border border-gray-100 hover:border-[#C5A55A] transition-colors" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-1878">
                    <div className="w-16 h-16 mx-auto bg-[#C5A55A]/20 text-[#9E8448] rounded-full flex items-center justify-center mb-6" data-id="element-1879">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1880">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1881">
                      {feature.desc}
                    </p>
                  </motion.div>) : [{
            title: 'Custom Design',
            desc: 'Choose from a variety of colors, patterns, and textures to match your style.',
            icon: <PaletteIcon className="w-8 h-8" data-id="element-1882" />
          }, {
            title: 'Low-Threshold Entry',
            desc: 'Safe and easy access to your shower without stepping over a high tub wall.',
            icon: <FootprintsIcon className="w-8 h-8" data-id="element-1883" />
          }, {
            title: 'Premium Accessories',
            desc: 'Customize with grab bars, seating, and built-in shelving options.',
            icon: <ShowerHeadIcon className="w-8 h-8" data-id="element-1884" />
          }, {
            title: 'Easy to Clean',
            desc: 'Durable materials that resist mold, mildew, and hard water stains.',
            icon: <SparklesIcon className="w-8 h-8" data-id="element-1885" />
          }].map((feature, idx) => <motion.div key={idx} className="bg-gray-50 p-8 rounded-2xl text-center border border-gray-100 hover:border-[#C5D93D] transition-colors" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-1886">
                    <div className="w-16 h-16 mx-auto bg-[#C5D93D]/20 text-[#9EAE31] rounded-full flex items-center justify-center mb-6" data-id="element-1887">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-1888">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1889">
                      {feature.desc}
                    </p>
                  </motion.div>)}
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-1890">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1891">
          <div className="text-center mb-16" data-id="element-1892">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-1893">
              Trusted {productLabel || 'Products'} with Fast, Expert
              Installation
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-1894">
            {[{
            title: 'Professional Installation',
            desc: 'Completed in as little as a day by KOHLER-certified specialists.',
            img: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop'
          }, {
            title: 'Industry Leading Safety',
            desc: 'Including ADA-compliant features, easily-accessible controls, and safety grab bars.',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Lifetime Limited Warranty',
            desc: 'Backed by KOHLER for long-term confidence and protection.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-1895">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-1896">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-1897" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1898">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1899">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-16" data-id="element-1900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1901">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-1902">
            <div className="flex flex-col gap-8" data-id="element-1903">
              <div className="flex flex-col" data-id="element-1904">
                <span className="text-3xl font-black text-white tracking-tight" data-id="element-1905">
                  KOHLER
                </span>
              </div>
              <p className="text-xs text-gray-400 font-medium" data-id="element-1906">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-1907">
              <div className="flex flex-col gap-4" data-id="element-1908">
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1909">
                  Home
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1910">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1911">
                  Terms Of Use
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-1912">
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1913">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-gray-300 hover:text-white font-medium" data-id="element-1914">
                  Do Not Sell My Personal Information
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8" data-id="element-1915">
            <p className="text-[10px] text-gray-500 leading-relaxed text-justify" data-id="element-1916">
              All estimates are free and no-obligation. Actual project costs may
              vary based on scope, materials, and local conditions. Licensed and
              insured. Contact us for full warranty details.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}